import { create, all } from 'mathjs';

const math = create(all);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SubPod {
  plaintext: string;
  plotExpression?: string;
  plotXMin?: number;
  plotXMax?: number;
}

export interface ResultPod {
  title: string;
  subpods: SubPod[];
}

export interface ComputeResult {
  success: boolean;
  pods: ResultPod[];
  inputInterpretation?: string;
  error?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format a number — snap to integer when floating-point noise is present */
function fmt(val: unknown): string {
  try {
    if (typeof val === 'number') {
      if (!isFinite(val)) return String(val);
      // Snap to integer if within 1e-9
      const nearInt = Math.round(val);
      if (Math.abs(val - nearInt) < 1e-9 && Math.abs(val) < 1e12) return String(nearInt);
      if (Math.abs(val) < 1e-12) return '0';
      // Trim floating-point noise
      const s = parseFloat(val.toPrecision(10));
      return String(s);
    }
    return math.format(val, { precision: 10 });
  } catch {
    return String(val);
  }
}

function toLatex(exprStr: string): string {
  try {
    return `$${math.parse(exprStr).toTex()}$`;
  } catch {
    return exprStr;
  }
}

function fmtLatex(val: unknown): string {
  try {
    const s = fmt(val);
    const n = Number(s);
    if (!isNaN(n)) return `$${n}$`;
    return `$${math.parse(String(val)).toTex()}$`;
  } catch {
    return fmt(val);
  }
}

function safeEval(expr: string, scope?: Record<string, unknown>): unknown {
  return math.evaluate(expr, scope ?? {});
}

function numDeriv(f: (x: number) => number, x: number): number {
  const h = 1e-7;
  return (f(x + h) - f(x - h)) / (2 * h);
}

function simpsons(f: (x: number) => number, a: number, b: number, n = 1000): number {
  let steps = n % 2 !== 0 ? n + 1 : n;
  const h = (b - a) / steps;
  let sum = f(a) + f(b);
  for (let i = 1; i < steps; i++) sum += (i % 2 === 0 ? 2 : 4) * f(a + i * h);
  return (h / 3) * sum;
}

function newtonRaphson(f: (x: number) => number, x0: number): number | null {
  let x = x0;
  for (let i = 0; i < 200; i++) {
    const fx = f(x);
    const fpx = numDeriv(f, x);
    if (Math.abs(fpx) < 1e-14) break;
    const xn = x - fx / fpx;
    if (Math.abs(xn - x) < 1e-11) return xn;
    x = xn;
  }
  return null;
}

/** Comprehensive root finding: integers −15..15 + fractions + large */
function findRoots(f: (x: number) => number): number[] {
  const roots: number[] = [];
  const starts: number[] = [];
  for (let i = -15; i <= 15; i++) starts.push(i);       // integers −15..15
  for (const v of [-0.5, 0.5, -0.25, 0.25, -0.75, 0.75, -1.5, 1.5, -2.5, 2.5, -7.5, 7.5, 25, -25, 50, -50]) starts.push(v);

  for (const x0 of starts) {
    const r = newtonRaphson(f, x0);
    if (r !== null && isFinite(r) && !roots.some(e => Math.abs(e - r) < 1e-6)) {
      roots.push(r);
    }
  }
  return roots.sort((a, b) => a - b);
}

function extractNumbers(str: string): number[] {
  const matches = str.match(/-?\d+(\.\d+)?/g);
  return matches ? matches.map(Number).filter(n => isFinite(n)) : [];
}

function cleanExpr(expr: string): string {
  return expr.replace(/\s+d[xyztu]\s*$/i, '').replace(/\*\*/g, '^').trim();
}

function detectVar(expr: string): string {
  for (const v of ['x', 'y', 'z', 't', 'n', 'u']) {
    if (new RegExp(`\\b${v}\\b`).test(expr)) return v;
  }
  return 'x';
}

/** Extract a named parameter from query: "radius 7", "r=7", "r = 7" */
function extractParam(query: string, names: string[]): number | null {
  for (const name of names) {
    const m = query.match(new RegExp(`(?:${name})\\s*[=:of with]*\\s*([+-]?\\d+(?:\\.\\d+)?)`, 'i'));
    if (m) return parseFloat(m[1]);
  }
  return null;
}

// ── Intent detection ──────────────────────────────────────────────────────────

function detectIntent(q: string): string {
  const lower = q.toLowerCase().trim();
  if (/\bsolve\b/.test(lower) && /[=]/.test(q)) return 'solve';
  if (/\bsolve\b/.test(lower)) return 'solve';
  if (/\bderivative\b|\bd\/dx\b|\bdiff(erentiate)?\b/.test(lower)) return 'derivative';
  if (/\bintegrat(e|ion|ing|al)?\b/.test(lower)) return 'integrate';
  if (/\bplot\b|\bgraph\b|\bsketch\b/.test(lower)) return 'plot';
  if (/\bmean\b|\bmedian\b|\bstd\b|\bstandard dev\b|\bvariance\b|\bstatistic/.test(lower)) return 'stats';
  if (/\bsimplify\b/.test(lower)) return 'simplify';
  if (/\bfactor\b/.test(lower)) return 'factor';
  if (/\bexpand\b/.test(lower)) return 'expand';
  if (/\blimit\b/.test(lower)) return 'limit';
  if (/\btaylor\b|\bmaclaurin\b/.test(lower)) return 'taylor';
  // Unit conversions — check before generic evaluate
  if (/\bconvert\b/i.test(lower) && /\bto\b|\bin\b|\binto\b/i.test(lower)) return 'convert';
  if (/\d+\s*°\s*[cfk]\b|\d+\s*(celsius|fahrenheit|kelvin)\s+(to|in|into)\b/i.test(lower)) return 'convert';
  if (/\b\d+(\.\d+)?\s+(km|miles|meters?|feet|foot|ft|inches?|cm|mm|kg|lbs?|pounds?|grams?|oz|ounces?|liters?|gallons?|mph|kph|m\/s|psi|atm|kwh|joules?|calories?)\s+(to|in|into)\b/i.test(lower)) return 'convert';
  // Geometry
  if (/\barea\b|\bvolume\b|\bperimeter\b|\bcircumference\b|\bsurface area\b|\bpythagorean\b|\bhypotenuse\b|\bradius\b|\bdiameter\b/.test(lower)) return 'geometry';
  // Physics
  if (/\bkinetic energy\b|\bpotential energy\b|\bf\s*=\s*ma\b|\bnewton\b|\bvelocity\b|\bacceleration\b|\bohm\b|\bvoltage\b|\bcurrent\b|\bresistance\b|\bspeed of light\b|\bmomentum\b|\bwork\b|\bpower\b|\bgravity\b/.test(lower)) return 'physics';
  // Distributions
  if (/\bnormal distribution\b|\bgaussian\b|\bbinomial distribution\b|\bpoisson\b/.test(lower)) return 'distribution';
  // Chemistry basics
  if (/\bmolar mass\b|\bph\s+of\b|\bbalance\b|\bstoichiometry\b/.test(lower) || (/(?:→|->)/.test(q) && /[A-Z][a-z]?\d*/.test(q))) return 'chemistry';
  return 'evaluate';
}

// ── Handlers ──────────────────────────────────────────────────────────────────

function handleEvaluate(query: string): ComputeResult {
  const expr = cleanExpr(query);
  let result: unknown;
  try {
    result = safeEval(expr);
  } catch {
    return { success: false, pods: [], error: `Could not evaluate: "${expr}". Try a specific math expression like "2^10" or "sin(pi/4)".` };
  }
  const resultStr = fmt(result);
  const pods: ResultPod[] = [
    { title: 'Input', subpods: [{ plaintext: toLatex(expr) }] },
    { title: 'Result', subpods: [{ plaintext: fmtLatex(result) }] },
  ];
  const numVal = Number(resultStr);
  if (isFinite(numVal) && !Number.isInteger(numVal)) {
    try {
      const frac = math.fraction(numVal) as { n: number; d: number };
      if (Math.abs(Number(frac) - numVal) < 1e-10 && frac.d !== 1) {
        pods.push({ title: 'Exact Fraction', subpods: [{ plaintext: `$\\frac{${frac.n}}{${frac.d}}$` }] });
      }
    } catch { /* ignore */ }
  }
  return { success: true, pods, inputInterpretation: expr };
}

function handleDerivative(query: string): ComputeResult {
  let expr = query
    .replace(/derivative\s+of\s+/i, '')
    .replace(/d\/dx\s*/i, '')
    .replace(/differentiate\s+/i, '')
    .replace(/diff\s+/i, '')
    .trim();
  expr = cleanExpr(expr);

  let variable = 'x';
  const varMatch = query.match(/with\s+respect\s+to\s+([a-z])/i);
  if (varMatch) variable = varMatch[1].toLowerCase();
  else variable = detectVar(expr);

  try {
    const parsed = math.parse(expr);
    const derivNode = math.derivative(parsed, variable);
    const simplified = math.simplify(derivNode);
    const derivTex = simplified.toTex();
    const inputTex = parsed.toTex();
    const scope: Record<string, number> = {};
    const checks: string[] = [];
    for (const xv of [0, 1, -1, 2]) {
      try {
        scope[variable] = xv;
        const val = math.evaluate(expr, scope);
        const dval = math.evaluate(simplified.toString(), scope);
        if (isFinite(Number(val)) && isFinite(Number(dval))) {
          checks.push(`${variable}=${xv}: $f(${xv}) = ${fmt(val)}$, $f'(${xv}) = ${fmt(dval)}$`);
          if (checks.length >= 3) break;
        }
      } catch { /* skip */ }
    }
    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$\\frac{d}{d${variable}}\\left(${inputTex}\\right)$` }] },
      { title: 'Derivative', subpods: [{ plaintext: `$$${derivTex}$$` }] },
      {
        title: 'Step-by-Step', subpods: [{
          plaintext: [
            `Differentiating $${inputTex}$ with respect to $${variable}$:`,
            '',
            '• Sum/difference rule: $\\frac{d}{d' + variable + '}[f \\pm g] = f\' \\pm g\'$',
            '• Power rule: $\\frac{d}{d' + variable + '}[' + variable + '^n] = n\\cdot ' + variable + '^{n-1}$',
            '• Chain/product rules applied where needed',
            '',
            `Result: $${derivTex}$`,
          ].join('\n'),
        }],
      },
    ];
    if (checks.length > 0) pods.push({ title: 'Numerical Verification', subpods: [{ plaintext: checks.join('\n') }] });
    pods.push({ title: 'Plot', subpods: [{ plaintext: `$y = ${inputTex}$ and its derivative`, plotExpression: expr, plotXMin: -5, plotXMax: 5 }] });
    return { success: true, pods, inputInterpretation: `d/d${variable}(${expr})` };
  } catch {
    return { success: false, pods: [], error: `Could not differentiate "${expr}". Check the expression syntax — use * for multiplication, ^ for powers.` };
  }
}

function handleIntegrate(query: string): ComputeResult {
  let expr = query
    .replace(/integrat(e|ion|al)?\s+(of\s+)?/i, '')
    .replace(/\bintegral\s+(of\s+)?/i, '')
    .trim();

  let a: number | null = null;
  let b: number | null = null;
  const boundsMatch = expr.match(/from\s+([-\d.]+)\s+to\s+([-\d.]+)/i);
  if (boundsMatch) {
    a = parseFloat(boundsMatch[1]);
    b = parseFloat(boundsMatch[2]);
    expr = expr.replace(boundsMatch[0], '').trim();
  }
  expr = cleanExpr(expr);

  const variable = detectVar(expr);
  const isDefinite = a !== null && b !== null;
  const lo = isDefinite ? a! : 0;
  const hi = isDefinite ? b! : 1;

  try {
    const parsed = math.parse(expr);
    const inputTex = parsed.toTex();
    const f = (xv: number): number => {
      const scope: Record<string, number> = {};
      scope[variable] = xv;
      return Number(math.evaluate(expr, scope));
    };
    const result = simpsons(f, lo, hi);
    const pods: ResultPod[] = [
      {
        title: 'Input',
        subpods: [{ plaintext: isDefinite ? `$\\int_{${lo}}^{${hi}} \\left(${inputTex}\\right)\\, d${variable}$` : `$\\int \\left(${inputTex}\\right)\\, d${variable}$` }],
      },
      {
        title: isDefinite ? 'Definite Integral Result' : 'Numerical Result (0 to 1)',
        subpods: [{
          plaintext: isDefinite
            ? `$$\\int_{${lo}}^{${hi}} \\left(${inputTex}\\right)\\, d${variable} \\approx ${fmt(result)}$$`
            : `$$\\int_{0}^{1} \\left(${inputTex}\\right)\\, d${variable} \\approx ${fmt(result)}$$\n\n(No bounds given — computed numerically from 0 to 1. Add "from a to b" for a definite result.)`,
        }],
      },
      {
        title: 'Method',
        subpods: [{
          plaintext: [
            isDefinite
              ? `Definite integral $\\int_{${lo}}^{${hi}} ${inputTex}\\, d${variable}$`
              : `Numerical integration of $${inputTex}\\, d${variable}$`,
            '',
            `Method: Simpson's Rule (n = 1000 subintervals)`,
            `• Split $[${lo},\\, ${hi}]$ into equal strips of width $h$`,
            `• Apply: $\\int_a^b f \\approx \\frac{h}{3}\\left[f(a) + 4f(x_1) + 2f(x_2) + \\cdots + f(b)\\right]$`,
            '',
            `Result: $\\approx ${fmt(result)}$`,
          ].join('\n'),
        }],
      },
      {
        title: 'Plot',
        subpods: [{ plaintext: `$y = ${inputTex}$`, plotExpression: expr, plotXMin: lo - Math.max(1, (hi - lo) * 0.4), plotXMax: hi + Math.max(1, (hi - lo) * 0.4) }],
      },
    ];
    return { success: true, pods, inputInterpretation: isDefinite ? `∫(${expr}) d${variable} from ${lo} to ${hi}` : `∫(${expr}) d${variable}` };
  } catch {
    return { success: false, pods: [], error: `Could not integrate "${expr}". Try: "integrate x^2 from 0 to 3"` };
  }
}

function handleSolve(query: string): ComputeResult {
  let expr = query.replace(/^solve\s+/i, '').trim();
  let lhs = expr;
  if (expr.includes('=')) {
    const parts = expr.split('=');
    lhs = `(${parts[0].trim()}) - (${parts[1].trim()})`;
  }
  lhs = cleanExpr(lhs);
  const variable = detectVar(lhs);

  // Try quadratic formula if function is purely quadratic (degree-2 polynomial in one variable)
  let exactRoots: number[] | null = null;
  try {
    const fAt = (xv: number): number => { const s: Record<string, number> = {}; s[variable] = xv; return Number(math.evaluate(lhs, s)); };
    const c = fAt(0), f1 = fAt(1), fm1 = fAt(-1);
    const a = (f1 + fm1 - 2 * c) / 2;
    const bCoef = (f1 - fm1) / 2;
    // Verify it's truly quadratic (not higher degree)
    const isQuad = [2, 3, 4].every(xv => Math.abs(fAt(xv) - (a * xv * xv + bCoef * xv + c)) < 1e-6);
    if (isQuad && Math.abs(a) > 1e-10) {
      const disc = bCoef * bCoef - 4 * a * c;
      if (disc < 0) {
        const realPart = fmt(-bCoef / (2 * a));
        const imagPart = fmt(Math.sqrt(-disc) / (2 * a));
        const inputDisplay = expr.includes('=') ? expr : `${expr} = 0`;
        return {
          success: true,
          pods: [
            { title: 'Input', subpods: [{ plaintext: `$${inputDisplay}$` }] },
            { title: 'Solutions', subpods: [{ plaintext: `$${variable} = ${realPart} \\pm ${imagPart}i$ (complex, no real solutions)` }] },
            { title: 'Discriminant', subpods: [{ plaintext: `$\\Delta = b^2 - 4ac = ${fmt(disc)} < 0$\n\nNegative discriminant → no real roots. The parabola doesn\'t cross the x-axis.` }] },
          ],
          inputInterpretation: expr,
        };
      }
      if (disc === 0) exactRoots = [-bCoef / (2 * a)];
      else {
        const sq = Math.sqrt(disc);
        exactRoots = [(-bCoef - sq) / (2 * a), (-bCoef + sq) / (2 * a)].sort((x, y) => x - y);
      }
    }
  } catch { /* not quadratic, fall through */ }

  try {
    const f = (xv: number): number => { const s: Record<string, number> = {}; s[variable] = xv; return Number(math.evaluate(lhs, s)); };
    const roots = exactRoots ?? findRoots(f);

    if (roots.length === 0) {
      return { success: false, pods: [], error: `No real solutions found for "${expr}". The equation may have only complex solutions.` };
    }

    const inputDisplay = expr.includes('=') ? expr : `${expr} = 0`;
    const rootTexList = roots.map(r => `$${variable} = ${fmt(r)}$`);

    let parsedTex = inputDisplay;
    try { parsedTex = math.parse(inputDisplay.replace('=', '-(') + ')').toTex(); } catch { /* keep raw */ }

    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `Solving: $${inputDisplay}$` }] },
      { title: 'Solutions', subpods: [{ plaintext: rootTexList.join('\n') }] },
      {
        title: 'Verification',
        subpods: [{
          plaintext: roots.map((r, i) => {
            const check = f(r);
            return `Root ${i + 1}: $${variable} = ${fmt(r)}$ → $f(${fmt(r)}) = ${fmt(check)}$ ${Math.abs(check) < 1e-6 ? '✓' : '(approx)'}`;
          }).join('\n'),
        }],
      },
      {
        title: 'Plot',
        subpods: [{
          plaintext: `Roots of $y = ${lhs}$`,
          plotExpression: lhs,
          plotXMin: Math.min(...roots) - 3,
          plotXMax: Math.max(...roots) + 3,
        }],
      },
    ];
    return { success: true, pods, inputInterpretation: expr };
  } catch {
    return { success: false, pods: [], error: `Could not solve "${expr}". Use standard form like "solve x^2 - 5x + 6 = 0"` };
  }
}

function handleStats(query: string): ComputeResult {
  const nums = extractNumbers(query);
  if (nums.length < 2) return { success: false, pods: [], error: 'Provide at least 2 numbers. Example: "Mean of {2, 4, 6, 8, 10}"' };

  const sorted = [...nums].sort((a, b) => a - b);
  const n = nums.length;
  const meanVal = math.mean(nums) as number;
  const medianVal = math.median(nums) as number;
  const stdVal = math.std(nums, 'uncorrected') as unknown as number;
  const varVal = math.variance(nums, 'uncorrected') as unknown as number;
  const minVal = math.min(...nums) as number;
  const maxVal = math.max(...nums) as number;

  const pods: ResultPod[] = [
    { title: 'Input Data', subpods: [{ plaintext: `$\\{${nums.join(',\\, ')}\\}$\n\nCount: $n = ${n}$` }] },
    {
      title: 'Basic Statistics', subpods: [{
        plaintext: [
          `Mean: $\\bar{x} = ${fmt(meanVal)}$`,
          `Median: $\\tilde{x} = ${fmt(medianVal)}$`,
          `Std Dev (pop.): $\\sigma = ${fmt(stdVal)}$`,
          `Variance: $\\sigma^2 = ${fmt(varVal)}$`,
          `Min: $${fmt(minVal)}$ · Max: $${fmt(maxVal)}$ · Range: $${fmt(maxVal - minVal)}$`,
        ].join('\n'),
      }],
    },
    { title: 'Sorted Data', subpods: [{ plaintext: `$\\{${sorted.join(',\\, ')}\\}$` }] },
    {
      title: 'Summary', subpods: [{
        plaintext: [
          `Sum: $${fmt(math.sum(...nums) as number)}$ · Count: $${n}$`,
          `Mean formula: $\\bar{x} = \\frac{${fmt(math.sum(...nums) as number)}}{${n}} = ${fmt(meanVal)}$`,
          `Variance: $\\sigma^2 = ${fmt(varVal)}$`,
          `Standard Deviation: $\\sigma = ${fmt(stdVal)}$`,
        ].join('\n'),
      }],
    },
  ];
  return { success: true, pods, inputInterpretation: `Statistics of {${nums.join(', ')}}` };
}

function handleSimplify(query: string): ComputeResult {
  let expr = query.replace(/^simplify\s+/i, '').trim();
  expr = cleanExpr(expr);
  try {
    const parsed = math.parse(expr);
    const simplified = math.simplify(expr);
    const inputTex = parsed.toTex();
    const simplTex = simplified.toTex();
    const changed = simplified.toString() !== parsed.toString();
    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$${inputTex}$` }] },
      { title: changed ? 'Simplified Form' : 'Result', subpods: [{ plaintext: `$$${simplTex}$$` }] },
    ];
    const checks: string[] = [];
    for (const xv of [1, 2, 3]) {
      try {
        const orig = math.evaluate(expr, { x: xv });
        const simp = math.evaluate(simplified.toString(), { x: xv });
        if (Math.abs(Number(orig) - Number(simp)) < 1e-8) checks.push(`At $x = ${xv}$: $${fmt(orig)}$ ✓`);
      } catch { /* skip */ }
    }
    if (checks.length > 0) pods.push({ title: 'Verification', subpods: [{ plaintext: 'Original and simplified agree:\n' + checks.join('\n') }] });
    return { success: true, pods, inputInterpretation: `simplify(${expr})` };
  } catch {
    return { success: false, pods: [], error: `Could not simplify "${expr}". Check expression syntax.` };
  }
}

function handleFactor(query: string): ComputeResult {
  let expr = query.replace(/^factor\s+/i, '').trim();
  expr = cleanExpr(expr);
  try {
    const parsed = math.parse(expr);
    const inputTex = parsed.toTex();
    const variable = detectVar(expr);
    const f = (xv: number): number => { const s: Record<string, number> = {}; s[variable] = xv; return Number(math.evaluate(expr, s)); };
    const roots = findRoots(f);

    let factorStr = `$${math.simplify(expr).toTex()}$`;
    if (roots.length > 0) {
      // Leading coefficient
      let a = 1;
      try {
        const xBig = 1000;
        const fBig = f(xBig);
        const prod = roots.reduce((acc, r) => acc * (xBig - r), 1);
        if (Math.abs(prod) > 1e-10) a = fBig / prod;
      } catch { /* skip */ }

      const aRounded = Math.round(a * 1e8) / 1e8;
      const factors = roots.map(r => {
        const rounded = Math.round(r * 1e10) / 1e10;
        if (rounded === 0) return `${variable}`;
        const neg = -rounded;
        return `(${variable} ${neg >= 0 ? '+' : '-'} ${Math.abs(neg)})`;
      });
      factorStr = Math.abs(aRounded - 1) < 1e-9
        ? `$${factors.join(' \\cdot ')}$`
        : `$${aRounded} \\cdot ${factors.join(' \\cdot ')}$`;
    }

    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$${inputTex}$` }] },
      { title: 'Factored Form', subpods: [{ plaintext: factorStr }] },
    ];
    if (roots.length > 0) {
      pods.push({ title: 'Roots (Zeros)', subpods: [{ plaintext: roots.map(r => `$${variable} = ${fmt(r)}$`).join('\n') }] });
      pods.push({ title: 'Plot', subpods: [{ plaintext: `$y = ${inputTex}$`, plotExpression: expr, plotXMin: Math.min(...roots) - 3, plotXMax: Math.max(...roots) + 3 }] });
    }
    return { success: true, pods, inputInterpretation: `factor(${expr})` };
  } catch {
    return { success: false, pods: [], error: `Could not factor "${expr}".` };
  }
}

function handleExpand(query: string): ComputeResult {
  let expr = query.replace(/^expand\s+/i, '').trim();
  expr = cleanExpr(expr);
  try {
    const parsed = math.parse(expr);
    const expanded = math.simplify(expr, math.simplify.rules);
    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$${parsed.toTex()}$` }] },
      { title: 'Expanded Form', subpods: [{ plaintext: `$$${expanded.toTex()}$$` }] },
    ];
    return { success: true, pods, inputInterpretation: `expand(${expr})` };
  } catch {
    return { success: false, pods: [], error: `Could not expand "${expr}".` };
  }
}

function handleLimit(query: string): ComputeResult {
  const asMatch = query.match(/as\s+([a-z])\s*->\s*([-\d.]+|infinity|inf|-infinity|-inf)/i);
  const ofMatch = query.match(/of\s+(.+?)(?:\s+as\s+|$)/i);
  if (!asMatch || !ofMatch) return { success: false, pods: [], error: 'Format: "limit as x->0 of sin(x)/x"' };

  const variable = asMatch[1].toLowerCase();
  const approachRaw = asMatch[2].toLowerCase();

  // Fix: correctly parse approach value (don't map -1 to -∞!)
  let approach: number;
  if (/^-inf/.test(approachRaw)) approach = -1e15;
  else if (/inf/.test(approachRaw)) approach = 1e15;
  else approach = parseFloat(approachRaw);   // handles -1, 0, 2.5, etc.

  let expr = ofMatch[1].trim();
  expr = cleanExpr(expr);

  try {
    const parsed = math.parse(expr);
    const inputTex = parsed.toTex();

    const deltas = [1e-3, 1e-5, 1e-7, 1e-9];
    const rightVals: number[] = [];
    const leftVals: number[] = [];

    for (const d of deltas) {
      try {
        const scopeR: Record<string, number> = {};
        const scopeL: Record<string, number> = {};
        scopeR[variable] = Math.abs(approach) > 1e14 ? (approach > 0 ? 1e15 - d : -1e15 + d) : approach + d;
        scopeL[variable] = Math.abs(approach) > 1e14 ? (approach > 0 ? 1e15 - d * 10 : -1e15 + d * 10) : approach - d;
        rightVals.push(Number(math.evaluate(expr, scopeR)));
        leftVals.push(Number(math.evaluate(expr, scopeL)));
      } catch { /* skip */ }
    }

    const rLimit = rightVals[rightVals.length - 1];
    const lLimit = leftVals[leftVals.length - 1];
    const bothConverge = rightVals.length >= 2 && leftVals.length >= 2
      && Math.abs(rightVals[rightVals.length - 1] - rightVals[rightVals.length - 2]) < 1e-5
      && Math.abs(leftVals[leftVals.length - 1] - leftVals[leftVals.length - 2]) < 1e-5;
    const limitsMatch = Math.abs(rLimit - lLimit) < 1e-5;
    const limitExists = bothConverge && limitsMatch && isFinite(rLimit);
    const approachDisplay = Math.abs(approach) > 1e14 ? (approach > 0 ? '\\infty' : '-\\infty') : String(approach);

    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$\\lim_{${variable} \\to ${approachDisplay}} ${inputTex}$` }] },
      {
        title: limitExists ? 'Limit' : 'Result',
        subpods: [{
          plaintext: limitExists
            ? `$$\\lim_{${variable} \\to ${approachDisplay}} ${inputTex} = ${fmt(rLimit)}$$`
            : limitsMatch ? `$$\\approx ${fmt(rLimit)}$$ (numerical estimate)`
            : `Left-hand limit $\\approx ${fmt(lLimit)}$, right-hand limit $\\approx ${fmt(rLimit)}$ — limit does not exist (DNE)`,
        }],
      },
      {
        title: 'Numerical Convergence',
        subpods: [{
          plaintext: deltas.map((d, i) =>
            rightVals[i] !== undefined && leftVals[i] !== undefined
              ? `$\\delta = ${d}$: left $= ${fmt(leftVals[i])}$, right $= ${fmt(rightVals[i])}$`
              : ''
          ).filter(Boolean).join('\n'),
        }],
      },
    ];
    return { success: true, pods, inputInterpretation: `lim(${variable}→${approachRaw}) ${expr}` };
  } catch {
    return { success: false, pods: [], error: `Could not compute limit of "${expr}".` };
  }
}

function handleTaylor(query: string): ComputeResult {
  // Handle both "Taylor series of ..." and "Maclaurin series of ..."
  let expr = query
    .replace(/\btaylor\s+(series\s+)?(expansion\s+)?of\s+/i, '')
    .replace(/\bmaclaurin\s+(series\s+)?(expansion\s+)?of\s+/i, '')
    .trim();

  const atMatch = expr.match(/\bat\s+([xyztn])\s*=\s*([-\d.]+)/i);
  const variable = atMatch ? atMatch[1].toLowerCase() : detectVar(expr);
  const a = atMatch ? parseFloat(atMatch[2]) : 0;
  expr = atMatch ? expr.replace(atMatch[0], '').trim() : expr;
  expr = cleanExpr(expr);

  try {
    const parsed = math.parse(expr);
    const inputTex = parsed.toTex();
    const n = 6;
    const coeffs: number[] = [];
    let factorial = 1;

    for (let k = 0; k < n; k++) {
      if (k > 0) factorial *= k;
      let fk = (xv: number): number => {
        const scope: Record<string, number> = {};
        scope[variable] = xv;
        return Number(math.evaluate(expr, scope));
      };
      for (let d = 0; d < k; d++) {
        const prev = fk;
        fk = (xv: number) => numDeriv(prev, xv);
      }
      coeffs.push(fk(a) / factorial);
    }

    const terms = coeffs.map((c, k) => {
      const rounded = Math.round(c * 1e8) / 1e8;
      if (Math.abs(rounded) < 1e-10) return null;
      if (k === 0) return `${fmt(rounded)}`;
      const xPart = a !== 0 ? `(${variable} - ${a})` : variable;
      if (k === 1) return `${fmt(rounded)}${xPart}`;
      return `${fmt(rounded)}${xPart}^{${k}}`;
    }).filter(Boolean);

    const taylorTex = terms.join(' + ').replace(/\+ -/g, ' - ');
    const isMaclaurin = a === 0;
    const seriesLabel = isMaclaurin ? 'Maclaurin Series (first 6 terms)' : 'Taylor Series (first 6 terms)';

    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `${isMaclaurin ? 'Maclaurin' : 'Taylor'} series of $${inputTex}$ at $${variable} = ${a}$` }] },
      { title: seriesLabel, subpods: [{ plaintext: `$$${taylorTex} + \\cdots$$` }] },
      {
        title: 'Coefficients',
        subpods: [{
          plaintext: coeffs.map((c, k) =>
            `$a_{${k}} = \\frac{f^{(${k})}(${a})}{${k}!} = ${fmt(Math.round(c * 1e8) / 1e8)}$`
          ).join('\n'),
        }],
      },
      { title: 'Plot', subpods: [{ plaintext: `$y = ${inputTex}$ near $${variable} = ${a}$`, plotExpression: expr, plotXMin: a - 4, plotXMax: a + 4 }] },
    ];
    return { success: true, pods, inputInterpretation: `Taylor(${expr}, ${variable}=${a})` };
  } catch {
    return { success: false, pods: [], error: `Could not compute Taylor series of "${expr}". Try: "Taylor series of sin(x) at x=0"` };
  }
}

function handlePlot(query: string): ComputeResult {
  let expr = query.replace(/^(plot|graph|sketch)\s+/i, '').replace(/^y\s*=\s*/i, '').replace(/^f\s*\([xy]\)\s*=\s*/i, '').trim();
  expr = cleanExpr(expr);
  try {
    const parsed = math.parse(expr);
    const inputTex = parsed.toTex();
    const variable = detectVar(expr);
    const pods: ResultPod[] = [
      { title: 'Input', subpods: [{ plaintext: `$y = ${inputTex}$` }] },
      { title: 'Plot', subpods: [{ plaintext: `Graph of $y = ${inputTex}$`, plotExpression: expr, plotXMin: -5, plotXMax: 5 }] },
      {
        title: 'Key Values', subpods: [{
          plaintext: [-3, -2, -1, 0, 1, 2, 3].map(xv => {
            try {
              const s: Record<string, number> = {}; s[variable] = xv;
              const v = Number(math.evaluate(expr, s));
              return isFinite(v) ? `$f(${xv}) = ${fmt(v)}$` : null;
            } catch { return null; }
          }).filter(Boolean).join('\n'),
        }],
      },
    ];
    return { success: true, pods, inputInterpretation: `plot(${expr})` };
  } catch {
    return { success: false, pods: [], error: `Could not plot "${expr}". Check the expression syntax.` };
  }
}

// ── Geometry Handler ──────────────────────────────────────────────────────────

function handleGeometry(query: string): ComputeResult {
  const q = query.toLowerCase();

  const geo = (title: string, formula: string, resultTex: string, resultVal: number, unit: string, extra?: ResultPod[]): ComputeResult => ({
    success: true,
    pods: [
      { title: 'Input', subpods: [{ plaintext: title }] },
      { title: 'Formula', subpods: [{ plaintext: `$${formula}$` }] },
      { title: 'Result', subpods: [{ plaintext: `$$${resultTex} \\approx ${fmt(resultVal)} \\text{ ${unit}}$$` }] },
      ...(extra ?? []),
    ],
    inputInterpretation: title,
  });

  // Circle area: "area of a circle with radius 7"
  if (/area.*circle|circle.*area/.test(q)) {
    const r = extractParam(query, ['radius', 'r']) ?? extractNumbers(query)[0];
    if (r != null) return geo(`Area of circle, r = ${r}`, 'A = \\pi r^2', `\\pi \\cdot ${r}^2`, Math.PI * r * r, 'sq units');
  }

  // Circumference / perimeter of circle
  if (/circumference|perimeter.*circle|circle.*perimeter/.test(q)) {
    const r = extractParam(query, ['radius', 'r']) ?? extractNumbers(query)[0];
    if (r != null) return geo(`Circumference of circle, r = ${r}`, 'C = 2\\pi r', `2\\pi \\cdot ${r}`, 2 * Math.PI * r, 'units');
  }

  // Sphere volume
  if (/volume.*sphere|sphere.*volume/.test(q)) {
    const r = extractParam(query, ['radius', 'r']) ?? extractNumbers(query)[0];
    if (r != null) return geo(`Volume of sphere, r = ${r}`, 'V = \\tfrac{4}{3}\\pi r^3', `\\tfrac{4}{3}\\pi \\cdot ${r}^3`, (4 / 3) * Math.PI * r ** 3, 'cu units');
  }

  // Sphere surface area
  if (/surface area.*sphere|sphere.*surface/.test(q)) {
    const r = extractParam(query, ['radius', 'r']) ?? extractNumbers(query)[0];
    if (r != null) return geo(`Surface area of sphere, r = ${r}`, 'SA = 4\\pi r^2', `4\\pi \\cdot ${r}^2`, 4 * Math.PI * r * r, 'sq units');
  }

  // Cylinder volume
  if (/volume.*cylinder|cylinder.*volume/.test(q)) {
    const nums = extractNumbers(query);
    const r = extractParam(query, ['radius', 'r']) ?? nums[0];
    const h = extractParam(query, ['height', 'h']) ?? nums[1];
    if (r != null && h != null) return geo(`Volume of cylinder, r = ${r}, h = ${h}`, 'V = \\pi r^2 h', `\\pi \\cdot ${r}^2 \\cdot ${h}`, Math.PI * r * r * h, 'cu units');
  }

  // Cylinder surface area
  if (/surface area.*cylinder|cylinder.*surface/.test(q)) {
    const nums = extractNumbers(query);
    const r = extractParam(query, ['radius', 'r']) ?? nums[0];
    const h = extractParam(query, ['height', 'h']) ?? nums[1];
    if (r != null && h != null) {
      const sa = 2 * Math.PI * r * r + 2 * Math.PI * r * h;
      return geo(`Surface area of cylinder, r = ${r}, h = ${h}`, 'SA = 2\\pi r^2 + 2\\pi r h', `2\\pi(${r}^2 + ${r} \\cdot ${h})`, sa, 'sq units');
    }
  }

  // Rectangle area/perimeter
  if (/area.*rectangle|rectangle.*area/.test(q)) {
    const nums = extractNumbers(query);
    const l = extractParam(query, ['length', 'l']) ?? nums[0];
    const w = extractParam(query, ['width', 'w']) ?? nums[1];
    if (l != null && w != null) return geo(`Area of rectangle, l = ${l}, w = ${w}`, 'A = l \\times w', `${l} \\times ${w}`, l * w, 'sq units');
  }
  if (/perimeter.*rectangle|rectangle.*perimeter/.test(q)) {
    const nums = extractNumbers(query);
    const l = extractParam(query, ['length', 'l']) ?? nums[0];
    const w = extractParam(query, ['width', 'w']) ?? nums[1];
    if (l != null && w != null) return geo(`Perimeter of rectangle, l = ${l}, w = ${w}`, 'P = 2(l + w)', `2(${l} + ${w})`, 2 * (l + w), 'units');
  }

  // Triangle area (base and height)
  if (/area.*triangle|triangle.*area/.test(q)) {
    const nums = extractNumbers(query);
    const b = extractParam(query, ['base', 'b']) ?? nums[0];
    const h = extractParam(query, ['height', 'h']) ?? nums[1];
    if (b != null && h != null) return geo(`Area of triangle, b = ${b}, h = ${h}`, 'A = \\tfrac{1}{2}bh', `\\tfrac{1}{2} \\cdot ${b} \\cdot ${h}`, 0.5 * b * h, 'sq units');
  }

  // Pythagorean theorem
  if (/pythagorean|hypotenuse/.test(q)) {
    const nums = extractNumbers(query);
    const a = extractParam(query, ['a']) ?? nums[0];
    const b = extractParam(query, ['b']) ?? nums[1];
    const c = extractParam(query, ['c']) ?? nums[2];
    if (a != null && b != null && c == null) {
      const hyp = Math.sqrt(a * a + b * b);
      return {
        success: true,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Pythagorean theorem: $a = ${a}$, $b = ${b}$, find $c$` }] },
          { title: 'Formula', subpods: [{ plaintext: `$c = \\sqrt{a^2 + b^2} = \\sqrt{${a}^2 + ${b}^2} = \\sqrt{${a * a + b * b}}$` }] },
          { title: 'Hypotenuse', subpods: [{ plaintext: `$$c = ${fmt(hyp)}$$` }] },
          { title: 'Verification', subpods: [{ plaintext: `$${a}^2 + ${b}^2 = ${a * a + b * b}$ and $\\sqrt{${a * a + b * b}} = ${fmt(hyp)}$ ✓` }] },
        ],
        inputInterpretation: `Pythagorean theorem a=${a} b=${b}`,
      };
    }
    if (a != null && c != null && b == null) {
      const side = Math.sqrt(c * c - a * a);
      return {
        success: true,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Pythagorean theorem: $a = ${a}$, $c = ${c}$, find $b$` }] },
          { title: 'Result', subpods: [{ plaintext: `$$b = \\sqrt{c^2 - a^2} = \\sqrt{${c * c - a * a}} = ${fmt(side)}$$` }] },
        ],
        inputInterpretation: `Pythagorean theorem a=${a} c=${c}`,
      };
    }
  }

  // Cone volume
  if (/volume.*cone|cone.*volume/.test(q)) {
    const nums = extractNumbers(query);
    const r = extractParam(query, ['radius', 'r']) ?? nums[0];
    const h = extractParam(query, ['height', 'h']) ?? nums[1];
    if (r != null && h != null) return geo(`Volume of cone, r = ${r}, h = ${h}`, 'V = \\tfrac{1}{3}\\pi r^2 h', `\\tfrac{1}{3}\\pi \\cdot ${r}^2 \\cdot ${h}`, (1 / 3) * Math.PI * r * r * h, 'cu units');
  }

  // Cube volume / surface area
  if (/volume.*cube|cube.*volume/.test(q)) {
    const s = extractParam(query, ['side', 's', 'edge']) ?? extractNumbers(query)[0];
    if (s != null) return geo(`Volume of cube, s = ${s}`, 'V = s^3', `${s}^3`, s ** 3, 'cu units');
  }

  return { success: false, pods: [], error: `Could not compute geometry for: "${query}". Try specifying the shape and dimensions, e.g. "area of a circle with radius 7".` };
}

// ── Physics Handler ───────────────────────────────────────────────────────────

function handlePhysics(query: string): ComputeResult {
  const q = query.toLowerCase();
  const nums = extractNumbers(query);

  // F = ma
  if (/f\s*=\s*ma|newton|force/.test(q) && !/speed of light|ohm|kinetic|potential/.test(q)) {
    const m = extractParam(query, ['m', 'mass']) ?? nums[0];
    const a = extractParam(query, ['a', 'acceleration']) ?? nums[1];
    if (m != null && a != null) {
      const F = m * a;
      return {
        success: true, inputInterpretation: `F = ma (m = ${m} kg, a = ${a} m/s²)`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Newton's Second Law: $m = ${m}$ kg, $a = ${a}$ m/s²` }] },
          { title: 'Formula', subpods: [{ plaintext: '$F = ma$' }] },
          { title: 'Result', subpods: [{ plaintext: `$$F = ${m} \\times ${a} = ${fmt(F)} \\text{ N}$$` }] },
          { title: 'Context', subpods: [{ plaintext: `A mass of ${m} kg accelerating at ${a} m/s² requires ${fmt(F)} Newtons of net force.\nNote: Earth gravity ≈ 9.81 m/s². Weight of ${m} kg = ${fmt(m * 9.81)} N.` }] },
        ],
      };
    }
  }

  // Kinetic energy KE = ½mv²
  if (/kinetic energy|ke\s*=/.test(q)) {
    const m = extractParam(query, ['m', 'mass']) ?? nums[0];
    const v = extractParam(query, ['v', 'velocity', 'speed']) ?? nums[1];
    if (m != null && v != null) {
      const ke = 0.5 * m * v * v;
      return {
        success: true, inputInterpretation: `KE (m = ${m} kg, v = ${v} m/s)`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Kinetic Energy: $m = ${m}$ kg, $v = ${v}$ m/s` }] },
          { title: 'Formula', subpods: [{ plaintext: '$KE = \\tfrac{1}{2}mv^2$' }] },
          { title: 'Result', subpods: [{ plaintext: `$$KE = \\tfrac{1}{2} \\cdot ${m} \\cdot ${v}^2 = ${fmt(ke)} \\text{ J}$$` }] },
        ],
      };
    }
  }

  // Potential energy PE = mgh
  if (/potential energy|pe\s*=|gravitational.*energy/.test(q)) {
    const m = extractParam(query, ['m', 'mass']) ?? nums[0];
    const h = extractParam(query, ['h', 'height']) ?? nums[1];
    const g = 9.81;
    if (m != null && h != null) {
      return {
        success: true, inputInterpretation: `PE (m = ${m} kg, h = ${h} m)`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Potential Energy: $m = ${m}$ kg, $h = ${h}$ m, $g = 9.81$ m/s²` }] },
          { title: 'Formula', subpods: [{ plaintext: '$PE = mgh$' }] },
          { title: 'Result', subpods: [{ plaintext: `$$PE = ${m} \\cdot 9.81 \\cdot ${h} = ${fmt(m * g * h)} \\text{ J}$$` }] },
        ],
      };
    }
  }

  // Ohm's Law
  if (/ohm|voltage|current|resistance/.test(q)) {
    const V = extractParam(query, ['V', 'voltage']) ?? nums[0];
    const R = extractParam(query, ['R', 'resistance']) ?? nums[1];
    const I = extractParam(query, ['I', 'current']);
    if (V != null && R != null) {
      const current = V / R;
      const power = V * current;
      return {
        success: true, inputInterpretation: `Ohm's Law (V = ${V} V, R = ${R} Ω)`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Ohm's Law: $V = ${V}$ V, $R = ${R}$ Ω` }] },
          { title: 'Formula', subpods: [{ plaintext: '$V = IR \\Rightarrow I = V/R$' }] },
          { title: 'Result', subpods: [{ plaintext: `$$I = \\frac{${V}}{${R}} = ${fmt(current)} \\text{ A}$$` }] },
          { title: 'Power', subpods: [{ plaintext: `$P = IV = ${fmt(current)} \\cdot ${V} = ${fmt(power)} \\text{ W}$` }] },
        ],
      };
    }
    if (I != null && R != null) {
      const volt = I * R;
      return {
        success: true, inputInterpretation: `Ohm's Law (I = ${I} A, R = ${R} Ω)`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Ohm's Law: $I = ${I}$ A, $R = ${R}$ Ω` }] },
          { title: 'Result', subpods: [{ plaintext: `$$V = IR = ${I} \\cdot ${R} = ${fmt(volt)} \\text{ V}$$` }] },
        ],
      };
    }
  }

  // Speed of light in medium
  if (/speed of light/.test(q)) {
    const c = 299792458; // m/s
    const nWater = 1.333, nGlass = 1.5;
    let medium = 'vacuum', speedVal = c, n = 1;
    if (/water/.test(q)) { medium = 'water'; n = nWater; speedVal = c / n; }
    else if (/glass/.test(q)) { medium = 'glass'; n = nGlass; speedVal = c / n; }
    return {
      success: true, inputInterpretation: `Speed of light in ${medium}`,
      pods: [
        { title: 'Input', subpods: [{ plaintext: `Speed of light in ${medium}` }] },
        { title: 'Formula', subpods: [{ plaintext: `$v = c / n$, where $n_{${medium}} = ${n}$` }] },
        { title: 'Result', subpods: [{ plaintext: `$$v = \\frac{${c}}{${n}} \\approx ${fmt(speedVal)} \\text{ m/s} \\approx ${fmt(speedVal / 1e6)} \\text{ Mm/s}$$` }] },
        { title: 'Context', subpods: [{ plaintext: `$c = ${c}$ m/s (exact, in vacuum)\nIn ${medium} (n = ${n}): $v \\approx ${fmt(speedVal / 1e6)}$ Mm/s (${fmt((speedVal / c) * 100)}% of c)` }] },
      ],
    };
  }

  // Momentum p = mv
  if (/momentum/.test(q)) {
    const m = extractParam(query, ['m', 'mass']) ?? nums[0];
    const v = extractParam(query, ['v', 'velocity', 'speed']) ?? nums[1];
    if (m != null && v != null) {
      return {
        success: true, inputInterpretation: `Momentum (m = ${m}, v = ${v})`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `Momentum: $m = ${m}$ kg, $v = ${v}$ m/s` }] },
          { title: 'Result', subpods: [{ plaintext: `$$p = mv = ${m} \\cdot ${v} = ${fmt(m * v)} \\text{ kg·m/s}$$` }] },
        ],
      };
    }
  }

  return { success: false, pods: [], error: `Could not compute physics for: "${query}". Try specifying values, e.g. "F = ma, m=5 a=9.8"` };
}

// ── Distribution Handler ──────────────────────────────────────────────────────

function handleDistribution(query: string): ComputeResult {
  const q = query.toLowerCase();
  const nums = extractNumbers(query);

  // Normal distribution
  if (/normal distribution|gaussian/.test(q)) {
    const mu = extractParam(query, ['mean', 'mu', 'μ', 'm']) ?? nums[0] ?? 0;
    const sigma = extractParam(query, ['sigma', 'std', 'σ', 's', 'sd']) ?? nums[1] ?? 1;
    const variance = sigma * sigma;
    const at1sigma = 68.27, at2sigma = 95.45, at3sigma = 99.73;
    return {
      success: true, inputInterpretation: `Normal distribution μ=${mu} σ=${sigma}`,
      pods: [
        { title: 'Input', subpods: [{ plaintext: `Normal distribution: $\\mu = ${mu}$, $\\sigma = ${sigma}$` }] },
        { title: 'PDF Formula', subpods: [{ plaintext: `$$f(x) = \\frac{1}{\\sigma\\sqrt{2\\pi}}e^{-\\frac{(x-\\mu)^2}{2\\sigma^2}}$$` }] },
        {
          title: 'Key Statistics', subpods: [{
            plaintext: [
              `Mean: $\\mu = ${mu}$`,
              `Standard Deviation: $\\sigma = ${sigma}$`,
              `Variance: $\\sigma^2 = ${fmt(variance)}$`,
              `Mode = Median = Mean = $${mu}$`,
            ].join('\n'),
          }],
        },
        {
          title: '68–95–99.7 Rule', subpods: [{
            plaintext: [
              `$P(\\mu - \\sigma < X < \\mu + \\sigma) = ${at1sigma}\\%$ → ($${mu - sigma}$ to $${mu + sigma}$)`,
              `$P(\\mu - 2\\sigma < X < \\mu + 2\\sigma) = ${at2sigma}\\%$ → ($${mu - 2 * sigma}$ to $${mu + 2 * sigma}$)`,
              `$P(\\mu - 3\\sigma < X < \\mu + 3\\sigma) = ${at3sigma}\\%$ → ($${mu - 3 * sigma}$ to $${mu + 3 * sigma}$)`,
            ].join('\n'),
          }],
        },
        {
          title: 'Plot', subpods: [{
            plaintext: `PDF of $N(${mu}, ${sigma}^2)$`,
            plotExpression: `(1 / (${sigma} * sqrt(2 * pi))) * exp(-((x - ${mu})^2) / (2 * ${sigma}^2))`,
            plotXMin: mu - 4 * sigma,
            plotXMax: mu + 4 * sigma,
          }],
        },
      ],
    };
  }

  // Binomial distribution
  if (/binomial distribution/.test(q)) {
    const n = Math.round(extractParam(query, ['n']) ?? nums[0] ?? 10);
    const p = extractParam(query, ['p']) ?? nums[1] ?? 0.5;
    const mean = n * p;
    const variance = n * p * (1 - p);
    const stdDev = Math.sqrt(variance);

    // Compute PMF for k = 0..n
    function binomCoeff(nn: number, kk: number): number {
      if (kk < 0 || kk > nn) return 0;
      let r = 1;
      for (let i = 0; i < kk; i++) r = r * (nn - i) / (i + 1);
      return r;
    }
    const pmfVals = Array.from({ length: Math.min(n + 1, 20) }, (_, k) => ({
      k,
      prob: binomCoeff(n, k) * Math.pow(p, k) * Math.pow(1 - p, n - k),
    }));
    const mode = Math.floor((n + 1) * p);

    return {
      success: true, inputInterpretation: `Binomial(n=${n}, p=${p})`,
      pods: [
        { title: 'Input', subpods: [{ plaintext: `Binomial distribution: $n = ${n}$, $p = ${p}$` }] },
        { title: 'PMF Formula', subpods: [{ plaintext: `$$P(X = k) = \\binom{${n}}{k} (${p})^k (${fmt(1 - p)})^{${n}-k}$$` }] },
        {
          title: 'Key Statistics', subpods: [{
            plaintext: [
              `Mean: $\\mu = np = ${n} \\cdot ${p} = ${fmt(mean)}$`,
              `Variance: $\\sigma^2 = np(1-p) = ${fmt(variance)}$`,
              `Std Dev: $\\sigma = ${fmt(stdDev)}$`,
              `Mode: $k = ${mode}$ ($P(k=${mode}) \\approx ${fmt(pmfVals[mode]?.prob ?? 0)}$)`,
            ].join('\n'),
          }],
        },
        {
          title: 'Probability Table', subpods: [{
            plaintext: pmfVals.slice(0, 10).map(({ k, prob }) =>
              `$P(X = ${k}) = ${fmt(Math.round(prob * 1e6) / 1e6)}$`
            ).join('\n'),
          }],
        },
      ],
    };
  }

  return { success: false, pods: [], error: 'Specify a distribution: "Normal distribution mean=0 sigma=1" or "Binomial distribution n=10 p=0.5"' };
}

// ── Unit Conversion Handler ───────────────────────────────────────────────────

// Base unit: meters
const LENGTH: Record<string, number> = {
  m: 1, meter: 1, meters: 1,
  km: 1000, kilometer: 1000, kilometers: 1000,
  cm: 0.01, centimeter: 0.01, centimeters: 0.01,
  mm: 0.001, millimeter: 0.001, millimeters: 0.001,
  mile: 1609.344, miles: 1609.344, mi: 1609.344,
  yard: 0.9144, yards: 0.9144, yd: 0.9144,
  foot: 0.3048, feet: 0.3048, ft: 0.3048,
  inch: 0.0254, inches: 0.0254, '"': 0.0254,
  'light-year': 9.461e15, ly: 9.461e15,
  'nautical mile': 1852, nm: 1852,
};
// Base unit: kg
const MASS: Record<string, number> = {
  kg: 1, kilogram: 1, kilograms: 1,
  g: 0.001, gram: 0.001, grams: 0.001,
  mg: 1e-6, milligram: 1e-6, milligrams: 1e-6,
  lb: 0.453592, lbs: 0.453592, pound: 0.453592, pounds: 0.453592,
  oz: 0.0283495, ounce: 0.0283495, ounces: 0.0283495,
  stone: 6.35029, stones: 6.35029, st: 6.35029,
  ton: 1000, tonne: 1000, 'metric ton': 1000,
  'short ton': 907.185, 'long ton': 1016.05,
};
// Base unit: liters
const VOLUME: Record<string, number> = {
  l: 1, liter: 1, liters: 1, litre: 1, litres: 1,
  ml: 0.001, milliliter: 0.001, milliliters: 0.001,
  gallon: 3.78541, gallons: 3.78541, gal: 3.78541,
  'us gallon': 3.78541, 'imperial gallon': 4.54609,
  pint: 0.473176, pints: 0.473176, pt: 0.473176,
  'fluid ounce': 0.0295735, 'fl oz': 0.0295735, floz: 0.0295735,
  cup: 0.236588, cups: 0.236588,
  quart: 0.946353, quarts: 0.946353, qt: 0.946353,
  tablespoon: 0.0147868, tablespoons: 0.0147868, tbsp: 0.0147868,
  teaspoon: 0.00492892, teaspoons: 0.00492892, tsp: 0.00492892,
  'm3': 1000, 'cubic meter': 1000, 'cubic meters': 1000,
};
// Base unit: m/s
const SPEED: Record<string, number> = {
  'm/s': 1, 'meters per second': 1, mps: 1,
  'km/h': 1 / 3.6, kph: 1 / 3.6, 'kmh': 1 / 3.6, 'km/hr': 1 / 3.6,
  mph: 0.44704, 'miles per hour': 0.44704,
  knot: 0.514444, knots: 0.514444, kt: 0.514444,
  'ft/s': 0.3048, 'feet per second': 0.3048,
  'mach': 340.29,
};
// Base unit: m²
const AREA: Record<string, number> = {
  'm2': 1, 'm²': 1, 'sq m': 1, 'square meter': 1, 'square meters': 1,
  'km2': 1e6, 'km²': 1e6, 'square km': 1e6, 'square kilometer': 1e6,
  'ft2': 0.092903, 'sq ft': 0.092903, 'square feet': 0.092903, 'square foot': 0.092903,
  'in2': 0.00064516, 'sq in': 0.00064516, 'square inch': 0.00064516, 'square inches': 0.00064516,
  'mi2': 2.59e6, 'sq mi': 2.59e6, 'square mile': 2.59e6, 'square miles': 2.59e6,
  acre: 4046.86, acres: 4046.86,
  hectare: 10000, hectares: 10000, ha: 10000,
  'cm2': 0.0001, 'sq cm': 0.0001, 'square centimeter': 0.0001,
};
// Base unit: Joules
const ENERGY: Record<string, number> = {
  j: 1, joule: 1, joules: 1,
  kj: 1000, kilojoule: 1000, kilojoules: 1000,
  cal: 4.184, calorie: 4.184, calories: 4.184,
  kcal: 4184, kilocalorie: 4184, kilocalories: 4184,
  kwh: 3.6e6, 'kilowatt-hour': 3.6e6, 'kilowatt hour': 3.6e6,
  btu: 1055.06, 'british thermal unit': 1055.06,
  ev: 1.602e-19,
};
// Base unit: Pa
const PRESSURE: Record<string, number> = {
  pa: 1, pascal: 1, pascals: 1,
  kpa: 1000, kilopascal: 1000,
  mpa: 1e6, megapascal: 1e6,
  bar: 1e5,
  mbar: 100, millibar: 100,
  atm: 101325, atmosphere: 101325, atmospheres: 101325,
  psi: 6894.76,
  torr: 133.322, mmhg: 133.322,
};
// Base unit: watts
const POWER: Record<string, number> = {
  w: 1, watt: 1, watts: 1,
  kw: 1000, kilowatt: 1000, kilowatts: 1000,
  mw: 1e6, megawatt: 1e6,
  hp: 745.7, horsepower: 745.7,
  'ft-lb/s': 1.35582,
};

// Temperature conversion (special - not linear to a common base)
function convertTemp(value: number, from: string, to: string): number | null {
  const f = from.replace('°', '').toLowerCase();
  const t = to.replace('°', '').toLowerCase();
  let celsius: number;
  if (['c', 'celsius', 'centigrade'].includes(f)) celsius = value;
  else if (['f', 'fahrenheit'].includes(f)) celsius = (value - 32) * 5 / 9;
  else if (['k', 'kelvin'].includes(f)) celsius = value - 273.15;
  else return null;
  if (['c', 'celsius', 'centigrade'].includes(t)) return celsius;
  if (['f', 'fahrenheit'].includes(t)) return celsius * 9 / 5 + 32;
  if (['k', 'kelvin'].includes(t)) return celsius + 273.15;
  return null;
}

function isTempUnit(u: string): boolean {
  const c = u.replace('°', '').toLowerCase();
  return ['c', 'f', 'k', 'celsius', 'fahrenheit', 'kelvin', 'centigrade'].includes(c);
}

function findUnitTable(u: string): [Record<string, number>, string] | null {
  const ul = u.toLowerCase().trim();
  if (LENGTH[ul] !== undefined) return [LENGTH, 'length'];
  if (MASS[ul] !== undefined) return [MASS, 'mass'];
  if (VOLUME[ul] !== undefined) return [VOLUME, 'volume'];
  if (SPEED[ul] !== undefined) return [SPEED, 'speed'];
  if (AREA[ul] !== undefined) return [AREA, 'area'];
  if (ENERGY[ul] !== undefined) return [ENERGY, 'energy'];
  if (PRESSURE[ul] !== undefined) return [PRESSURE, 'pressure'];
  if (POWER[ul] !== undefined) return [POWER, 'power'];
  return null;
}

function parseConversionQuery(query: string): { value: number; from: string; to: string } | null {
  let q = query.replace(/^convert\s+/i, '').trim();
  // Temperature with degree symbol: "100°F to Celsius", "100 F to C"
  const tempMatch = q.match(/^(-?\d+(?:\.\d+)?)\s*°?\s*(c|f|k|celsius|fahrenheit|kelvin|centigrade)\b\s+(?:to|in|into)\s+(c|f|k|celsius|fahrenheit|kelvin|centigrade)/i);
  if (tempMatch) return { value: parseFloat(tempMatch[1]), from: tempMatch[2], to: tempMatch[3] };
  // General: "VALUE FROM to TO" — greedy unit matching
  const gen = q.match(/^(-?\d+(?:\.\d+)?)\s+(.+?)\s+(?:to|in|into)\s+(.+)$/i);
  if (gen) return { value: parseFloat(gen[1]), from: gen[2].trim(), to: gen[3].trim() };
  // "VALUE FROM" alone (e.g. "5km in miles") parsed differently
  const alt = q.match(/^(-?\d+(?:\.\d+)?)\s*([a-z/²]+)\s+(?:to|in|into)\s+([a-z/²]+)/i);
  if (alt) return { value: parseFloat(alt[1]), from: alt[2], to: alt[3] };
  return null;
}

function handleConvert(query: string): ComputeResult {
  const parsed = parseConversionQuery(query);
  if (!parsed) {
    return { success: false, pods: [], error: 'Format: "convert 5 miles to km" or "100°F to Celsius"' };
  }
  const { value, from, to } = parsed;
  const fromL = from.toLowerCase().trim();
  const toL = to.toLowerCase().trim();

  // Temperature
  if (isTempUnit(fromL) || isTempUnit(toL)) {
    const result = convertTemp(value, fromL, toL);
    if (result === null) return { success: false, pods: [], error: `Unknown temperature units: "${from}" → "${to}".` };
    const fromLabel = fromL.replace(/\b\w/g, c => c.toUpperCase());
    const toLabel = toL.replace(/\b\w/g, c => c.toUpperCase());
    return {
      success: true,
      inputInterpretation: `${value}° ${fromLabel} to ${toLabel}`,
      pods: [
        { title: 'Input', subpods: [{ plaintext: `$${value}°\\text{ ${fromLabel}}$` }] },
        { title: 'Result', subpods: [{ plaintext: `$$${fmt(result)}°\\text{ ${toLabel}}$$` }] },
        {
          title: 'Formula', subpods: [{
            plaintext: ['C→F', 'celsius to fahrenheit', 'c to f'].some(s => s.includes(fromL[0]) && s.includes(toL[0]))
              ? `$F = C \\times \\frac{9}{5} + 32 = ${value} \\times 1.8 + 32 = ${fmt(result)}$`
              : ['F→C', 'fahrenheit to celsius', 'f to c'].some(s => s.includes(fromL[0]) && s.includes(toL[0]))
              ? `$C = (F - 32) \\times \\frac{5}{9} = (${value} - 32) \\times \\frac{5}{9} = ${fmt(result)}$`
              : `Result: $${fmt(result)}°\\text{ ${toLabel}}$`,
          }],
        },
        {
          title: 'Reference Points', subpods: [{
            plaintext: [
              'Common temperatures:',
              '$0°C = 32°F = 273.15K$ (water freezes)',
              '$100°C = 212°F = 373.15K$ (water boils)',
              '$37°C = 98.6°F$ (body temperature)',
              '$-40°C = -40°F$ (scales meet)',
            ].join('\n'),
          }],
        },
      ],
    };
  }

  // Linear unit conversion
  const fromTable = findUnitTable(fromL);
  const toTable = findUnitTable(toL);

  if (!fromTable) return { success: false, pods: [], error: `Unknown unit: "${from}". Try: km, miles, kg, lbs, °C, °F, m/s, mph, liters, gallons…` };
  if (!toTable) return { success: false, pods: [], error: `Unknown unit: "${to}". Try: km, miles, kg, lbs, °C, °F, m/s, mph, liters, gallons…` };
  if (fromTable[1] !== toTable[1]) return { success: false, pods: [], error: `Cannot convert "${from}" (${fromTable[1]}) to "${to}" (${toTable[1]}) — different dimensions.` };

  const [table, dimension] = fromTable;
  const factorFrom = table[fromL];
  const factorTo = toTable[0][toL];
  const result = value * factorFrom / factorTo;

  // Human-readable context
  const dimLabel = dimension.charAt(0).toUpperCase() + dimension.slice(1);
  const contextRows: string[] = [];
  // Show a few related conversions
  const relatedPairs: Record<string, [string, string][]> = {
    length: [['km', 'miles'], ['m', 'ft'], ['cm', 'inches']],
    mass: [['kg', 'lbs'], ['g', 'oz'], ['kg', 'oz']],
    volume: [['liter', 'gallon'], ['ml', 'fl oz'], ['liter', 'pint']],
    speed: [['km/h', 'mph'], ['m/s', 'km/h'], ['mph', 'm/s']],
    area: [['m2', 'ft2'], ['km2', 'mi2'], ['hectare', 'acre']],
    energy: [['kj', 'kcal'], ['kwh', 'j'], ['j', 'cal']],
    pressure: [['atm', 'psi'], ['bar', 'atm'], ['kpa', 'psi']],
    power: [['kw', 'hp'], ['w', 'kw'], ['hp', 'w']],
  };
  const pairs = relatedPairs[dimension] ?? [];
  for (const [a, b] of pairs.slice(0, 3)) {
    const fa = table[a] ?? toTable[0][a];
    const fb = table[b] ?? toTable[0][b];
    if (fa && fb) contextRows.push(`$1\\text{ ${a}} = ${fmt(fa / fb)}\\text{ ${b}}$`);
  }

  return {
    success: true,
    inputInterpretation: `${value} ${from} to ${to}`,
    pods: [
      { title: 'Input', subpods: [{ plaintext: `$${value}\\text{ ${from}}$` }] },
      { title: `${dimLabel} Conversion`, subpods: [{ plaintext: `$$${value}\\text{ ${from}} = ${fmt(result)}\\text{ ${to}}$$` }] },
      {
        title: 'Calculation', subpods: [{
          plaintext: `1 ${from} = ${fmt(factorFrom)} base units\n1 ${to} = ${fmt(factorTo)} base units\n\n$${value} \\times \\frac{${fmt(factorFrom)}}{${fmt(factorTo)}} = ${fmt(result)}\\text{ ${to}}$`,
        }],
      },
      ...(contextRows.length > 0 ? [{ title: 'Related Conversions', subpods: [{ plaintext: contextRows.join('\n') }] }] : []),
    ],
  };
}

// ── Chemistry ────────────────────────────────────────────────────────────────

// Atomic masses g/mol
const ATOMIC_MASSES: Record<string, number> = {
  H: 1.008, He: 4.003, Li: 6.941, Be: 9.012, B: 10.811, C: 12.011,
  N: 14.007, O: 15.999, F: 18.998, Ne: 20.180, Na: 22.990, Mg: 24.305,
  Al: 26.982, Si: 28.086, P: 30.974, S: 32.060, Cl: 35.453, Ar: 39.948,
  K: 39.098, Ca: 40.078, Sc: 44.956, Ti: 47.867, V: 50.942, Cr: 51.996,
  Mn: 54.938, Fe: 55.845, Co: 58.933, Ni: 58.693, Cu: 63.546, Zn: 65.38,
  Ga: 69.723, Ge: 72.630, As: 74.922, Se: 78.971, Br: 79.904, Kr: 83.798,
  Rb: 85.468, Sr: 87.62, Ag: 107.868, Sn: 118.710, I: 126.904,
  Ba: 137.327, Hg: 200.59, Au: 196.967, Pb: 207.2, Pt: 195.084, W: 183.84,
};

// Parse chemical formula with parentheses: "Ca(OH)2" → {Ca:1, O:2, H:2}
function parseChemFormula(formula: string): Record<string, number> {
  let idx = 0;
  function parseGroup(): Record<string, number> {
    const grp: Record<string, number> = {};
    while (idx < formula.length && formula[idx] !== ')') {
      if (formula[idx] === '(') {
        idx++;
        const inner = parseGroup();
        idx++; // skip ')'
        const ns = idx;
        while (idx < formula.length && /\d/.test(formula[idx])) idx++;
        const mult = idx > ns ? parseInt(formula.slice(ns, idx)) : 1;
        for (const [el, c] of Object.entries(inner)) grp[el] = (grp[el] ?? 0) + c * mult;
      } else if (/[A-Z]/.test(formula[idx])) {
        const s = idx++;
        while (idx < formula.length && /[a-z]/.test(formula[idx])) idx++;
        const el = formula.slice(s, idx);
        const ns = idx;
        while (idx < formula.length && /\d/.test(formula[idx])) idx++;
        const cnt = idx > ns ? parseInt(formula.slice(ns, idx)) : 1;
        grp[el] = (grp[el] ?? 0) + cnt;
      } else { idx++; }
    }
    return grp;
  }
  return parseGroup();
}

function gcdInt(a: number, b: number): number { return b === 0 ? Math.abs(a) : gcdInt(b, a % b); }
function lcmInt(a: number, b: number): number { return a === 0 || b === 0 ? 0 : Math.abs(a * b) / gcdInt(a, b); }

// Gaussian elimination → null vector (floating-point)
function findNullVector(matrix: number[][]): number[] | null {
  const rows = matrix.length;
  const cols = matrix[0].length;
  const M = matrix.map(r => [...r]);
  const pivCols: number[] = [];
  let pr = 0;
  for (let col = 0; col < cols && pr < rows; col++) {
    let best = pr;
    for (let r = pr + 1; r < rows; r++) if (Math.abs(M[r][col]) > Math.abs(M[best][col])) best = r;
    if (Math.abs(M[best][col]) < 1e-9) continue;
    [M[pr], M[best]] = [M[best], M[pr]];
    const sc = M[pr][col];
    for (let j = 0; j < cols; j++) M[pr][j] /= sc;
    for (let r = 0; r < rows; r++) {
      if (r !== pr && Math.abs(M[r][col]) > 1e-9) {
        const f = M[r][col];
        for (let j = 0; j < cols; j++) M[r][j] -= f * M[pr][j];
      }
    }
    pivCols.push(col); pr++;
  }
  const freeCols = Array.from({ length: cols }, (_, i) => i).filter(c => !pivCols.includes(c));
  if (freeCols.length === 0) return null;
  const x = new Array(cols).fill(0);
  x[freeCols[freeCols.length - 1]] = 1;
  for (let i = pivCols.length - 1; i >= 0; i--) {
    const pc = pivCols[i];
    let val = 0;
    for (let j = pc + 1; j < cols; j++) val += M[i][j] * x[j];
    x[pc] = -val;
  }
  return x;
}

// Scale float null vector to smallest positive integers
function floatToSmallIntegers(v: number[]): number[] | null {
  const maxD = 48;
  const fracs = v.map(val => {
    const sign = val < 0 ? -1 : 1;
    const abs = Math.abs(val);
    for (let d = 1; d <= maxD; d++) {
      const n = Math.round(abs * d);
      if (n > 0 && Math.abs(n / d - abs) < 1e-5) return [sign * n, d];
    }
    return [Math.round(val * maxD), maxD];
  });
  let dLcm = 1;
  for (const [, d] of fracs) dLcm = lcmInt(dLcm, d);
  const ints = fracs.map(([n, d]) => Math.round(n * dLcm / d));
  if (ints.some(x => x === 0)) return null;
  // If any negative, try flipping all
  const allPos = ints.every(x => x > 0);
  const allNeg = ints.every(x => x < 0);
  const normalized = allPos ? ints : allNeg ? ints.map(x => -x) : null;
  if (!normalized) return null;
  const g = normalized.reduce((acc, x) => gcdInt(acc, Math.abs(x)), 0);
  return normalized.map(x => x / g);
}

// Compute molar mass from parsed element map
function molarMassOf(elems: Record<string, number>): number {
  return Object.entries(elems).reduce((sum, [el, cnt]) => sum + (ATOMIC_MASSES[el] ?? 0) * cnt, 0);
}

// Balance a chemical equation and return full results + stoichiometry
function balanceEquation(query: string): ComputeResult {
  const raw = query.replace(/^(balance|stoichiometry)\s*/i, '').trim();
  const arrowMatch = raw.match(/^(.+?)\s*(?:→|->|=)\s*(.+)$/);
  if (!arrowMatch) return { success: false, pods: [], error: 'Format: "balance H2 + O2 → H2O" or "balance CH4 + O2 → CO2 + H2O"' };

  const parseSide = (side: string) =>
    side.split(/\s*\+\s*/).map(token => {
      const t = token.trim();
      const m = t.match(/^(\d+)\s*([A-Z].*)$/);
      return m ? { formula: m[2] } : { formula: t };
    });

  const reactants = parseSide(arrowMatch[1]);
  const products = parseSide(arrowMatch[2]);
  const all = [...reactants, ...products];
  const n = all.length;

  if (n < 2) return { success: false, pods: [], error: 'Equation needs at least 2 compounds.' };

  const compElems = all.map(({ formula }) => parseChemFormula(formula));
  const elemSet = new Set<string>();
  compElems.forEach(e => Object.keys(e).forEach(el => elemSet.add(el)));
  const elements = Array.from(elemSet).sort();

  // Build element×compound matrix (reactants +, products -)
  const matrix = elements.map(el =>
    all.map((_, j) => (j < reactants.length ? 1 : -1) * (compElems[j][el] ?? 0))
  );

  const nullVec = findNullVector(matrix);
  if (!nullVec) return { success: false, pods: [], error: `Could not solve the system for: "${raw}". Check formula spelling (capital first letter, e.g. H2O not h2o).` };

  const coeffs = floatToSmallIntegers(nullVec);
  if (!coeffs) return { success: false, pods: [], error: `Could not find integer coefficients for: "${raw}".` };

  const rCoeffs = coeffs.slice(0, reactants.length);
  const pCoeffs = coeffs.slice(reactants.length);

  const fmtCoeff = (c: number) => c > 1 ? String(c) : '';
  const rStr = reactants.map((c, i) => `${fmtCoeff(rCoeffs[i])}${c.formula}`).join(' + ');
  const pStr = products.map((c, i) => `${fmtCoeff(pCoeffs[i])}${c.formula}`).join(' + ');
  const rLatex = reactants.map((c, i) => `${fmtCoeff(rCoeffs[i])}\\text{${c.formula}}`).join(' + ');
  const pLatex = products.map((c, i) => `${fmtCoeff(pCoeffs[i])}\\text{${c.formula}}`).join(' + ');

  // Atom balance verification
  const verifyRows = elements.map(el => {
    const lhs = rCoeffs.reduce((s, c, i) => s + c * (compElems[i][el] ?? 0), 0);
    const rhs = pCoeffs.reduce((s, c, i) => s + c * (compElems[reactants.length + i][el] ?? 0), 0);
    return `${el}: ${lhs} → ${rhs} ${lhs === rhs ? '✓' : '✗'}`;
  });

  // Mole ratios + molar masses
  const masses = all.map((_, i) => molarMassOf(compElems[i]));
  const ratioLines = all.map((c, i) => {
    const mm = fmt(masses[i]);
    const side = i < reactants.length ? 'reactant' : 'product';
    return `${coeffs[i]} mol ${c.formula}  (M = ${mm} g/mol)  [${side}]`;
  });

  // Stoichiometry table: "per 1 mol of first reactant"
  const baseIdx = 0;
  const baseMol = coeffs[baseIdx];
  const stoichLines = all.map((c, i) => {
    const molRatio = fmt(coeffs[i] / baseMol);
    const massNeeded = fmt((coeffs[i] / baseMol) * masses[i]);
    const arrow = i < reactants.length ? 'needs' : 'produces';
    return `${arrow} ${molRatio} mol ${c.formula}  (= ${massNeeded} g)`;
  }).filter((_, i) => i !== baseIdx);

  // Mass balance check
  const reactMass = rCoeffs.reduce((s, c, i) => s + c * masses[i], 0);
  const prodMass = pCoeffs.reduce((s, c, i) => s + c * masses[reactants.length + i], 0);
  const massBalanced = Math.abs(reactMass - prodMass) < 0.5;

  // Common equation context
  const commonEqs: Record<string, string> = {
    'H2+O2->H2O': 'Hydrogen combustion — forms water.',
    'CH4+O2->CO2+H2O': 'Methane combustion — natural gas burning.',
    'C6H12O6+O2->CO2+H2O': 'Glucose oxidation (cellular respiration).',
    'Fe+O2->Fe2O3': 'Iron rusting (oxidation).',
    'N2+H2->NH3': 'Haber process — ammonia synthesis.',
    'HCl+NaOH->NaCl+H2O': 'Acid-base neutralization.',
    'C+O2->CO2': 'Carbon combustion.',
    'Al+O2->Al2O3': 'Aluminium oxidation.',
  };
  const normalizedRaw = raw.replace(/\s+/g, '').replace('→', '->');
  const context = Object.entries(commonEqs).find(([k]) => normalizedRaw.toLowerCase().startsWith(k.toLowerCase()));

  return {
    success: true,
    inputInterpretation: `Balance: ${raw}`,
    pods: [
      { title: 'Unbalanced Equation', subpods: [{ plaintext: `$${raw.replace(/→|->/g, '\\rightarrow').replace(/([A-Z][a-z]?\d*)/g, '\\text{$1}')}$` }] },
      { title: 'Balanced Equation', subpods: [{ plaintext: `$$${rLatex} \\rightarrow ${pLatex}$$\n\n${rStr} → ${pStr}` }] },
      { title: 'Coefficients', subpods: [{ plaintext: all.map((c, i) => `${c.formula} : ${coeffs[i]}`).join('\n') }] },
      { title: 'Mole Ratios & Molar Masses', subpods: [{ plaintext: ratioLines.join('\n') }] },
      {
        title: `Stoichiometry (per 1 mol ${all[baseIdx].formula})`,
        subpods: [{ plaintext: stoichLines.join('\n') }],
      },
      { title: 'Atom Balance Verification', subpods: [{ plaintext: verifyRows.join('\n') }] },
      {
        title: 'Mass Balance',
        subpods: [{
          plaintext: `Reactant side: ${fmt(reactMass)} g/mol\nProduct side:  ${fmt(prodMass)} g/mol\n${massBalanced ? 'Mass is conserved. ✓' : '⚠ Mass mismatch — check formula.'}`,
        }],
      },
      ...(context ? [{ title: 'Reaction Context', subpods: [{ plaintext: context[1] }] }] : []),
    ],
  };
}

function handleChemistry(query: string): ComputeResult {
  const q = query.toLowerCase();

  // Equation balancing
  if (/\bbalance\b|\bstoichiometry\b/.test(q) || /→|->/.test(query)) {
    return balanceEquation(query);
  }

  // Molar mass
  if (/molar mass/.test(q)) {
    const formulaMatch = query.match(/of\s+([A-Z][a-zA-Z0-9()[\]]+)/);
    if (formulaMatch) {
      const formula = formulaMatch[1];
      const elems = parseChemFormula(formula);
      const total = molarMassOf(elems);
      const breakdown = Object.entries(elems).map(([el, cnt]) => {
        const contrib = (ATOMIC_MASSES[el] ?? 0) * cnt;
        return `${el}${cnt > 1 ? `×${cnt}` : ''}: ${fmt(contrib)} g/mol`;
      });
      const unknowns = Object.keys(elems).filter(el => !ATOMIC_MASSES[el]);
      if (unknowns.length > 0) return { success: false, pods: [], error: `Unknown element(s): ${unknowns.join(', ')}` };
      if (total > 0) {
        return {
          success: true,
          inputInterpretation: `Molar mass of ${formula}`,
          pods: [
            { title: 'Input', subpods: [{ plaintext: `Molar mass of $\\text{${formula}}$` }] },
            { title: 'Breakdown', subpods: [{ plaintext: breakdown.join('\n') }] },
            { title: 'Molar Mass', subpods: [{ plaintext: `$$M(\\text{${formula}}) = ${fmt(total)}\\text{ g/mol}$$` }] },
          ],
        };
      }
    }
  }

  // pH calculation
  if (/ph\s+of/.test(q)) {
    const nums = extractNumbers(query);
    const conc = nums[0];
    if (conc != null && conc > 0) {
      const pH = -Math.log10(conc);
      const type = pH < 7 ? 'acidic' : pH > 7 ? 'basic' : 'neutral';
      return {
        success: true,
        inputInterpretation: `pH of ${conc} M solution`,
        pods: [
          { title: 'Input', subpods: [{ plaintext: `pH of $${conc}$ M strong acid solution` }] },
          { title: 'Formula', subpods: [{ plaintext: '$pH = -\\log_{10}[H^+]$' }] },
          { title: 'Result', subpods: [{ plaintext: `$$pH = -\\log_{10}(${conc}) = ${fmt(pH)}$$\n\nSolution is ${type} (pH ${type === 'acidic' ? '<' : type === 'basic' ? '>' : '='} 7).` }] },
          { title: 'pH Scale', subpods: [{ plaintext: 'pH < 7: acidic\npH = 7: neutral\npH > 7: basic/alkaline\npH range: 0 (strongest acid) to 14 (strongest base)' }] },
        ],
      };
    }
  }

  return { success: false, pods: [], error: `Could not compute. Try:\n• "balance H2 + O2 → H2O"\n• "molar mass of H2SO4"\n• "pH of 0.001 M HCl"` };
}

// ── Main entry point ──────────────────────────────────────────────────────────

export function computeLocally(query: string): ComputeResult {
  const intent = detectIntent(query);
  try {
    switch (intent) {
      case 'derivative':   return handleDerivative(query);
      case 'integrate':    return handleIntegrate(query);
      case 'solve':        return handleSolve(query);
      case 'stats':        return handleStats(query);
      case 'simplify':     return handleSimplify(query);
      case 'factor':       return handleFactor(query);
      case 'expand':       return handleExpand(query);
      case 'limit':        return handleLimit(query);
      case 'taylor':       return handleTaylor(query);
      case 'plot':         return handlePlot(query);
      case 'geometry':     return handleGeometry(query);
      case 'physics':      return handlePhysics(query);
      case 'distribution': return handleDistribution(query);
      case 'chemistry':    return handleChemistry(query);
      case 'convert':      return handleConvert(query);
      default:             return handleEvaluate(query);
    }
  } catch (e) {
    return { success: false, pods: [], error: `Computation error: ${e instanceof Error ? e.message : String(e)}` };
  }
}
